import React, { forwardRef, useImperativeHandle, useEffect, useState, useCallback, useRef } from 'react';
import { MobileView, BrowserView } from 'react-device-detect';
import axios from 'axios';
import DeckView from "../Index/Cards/DeckView";
import { useAuth } from '../../AuthContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsisV,
    faFileExport,
    faFileAlt,
    faCopy,
    faPlus
} from '@fortawesome/free-solid-svg-icons';
import "./PublicDeckList.css";

const PublicDeckList = forwardRef(({ cardsData, gameData }, ref) => {
    useImperativeHandle(ref, () => ({ resetDeckList: () => setDecks([]) }));

    const { user, userIsLoggedIn, host, port } = useAuth();
    const [abilities] = useState(cardsData?.abilities || []);
    const [decks, setDecks] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const decksPerPage = 10;
    const [totalPages, setTotalPages] = useState(1);
    // Decks are already paginated from backend
    const paginatedDecks = decks;
    const [deckPreviews, setDeckPreviews] = useState({});
    const [ownerNames, setOwnerNames] = useState({});
    const [selectedDeckData, setSelectedDeckData] = useState(null);
    const [isModalDeckViewVisible, setIsModalDeckViewVisible] = useState(false);
    const [errorDeckIndex] = useState(null);
    const [isKebabMenuOpen, setIsKebabMenuOpen] = useState({});

    const deckRefs = useRef({});
    const observerRef = useRef(null);
    const pendingQueue = useRef([]);
    const activeCount = useRef(0);
    const CONCURRENCY = 4;

    const toggleKebabMenu = (index) => setIsKebabMenuOpen(prev => ({ ...prev, [index]: !prev[index] }));

    // Close kebab menus when clicking outside
    useEffect(() => {
        const anyOpen = Object.values(isKebabMenuOpen || {}).some(Boolean);
        if (!anyOpen) return;
        const onDocClick = (e) => {
            try {
                const target = e.target;
                if (target && (target.closest('.deck-kebab-menu') || target.closest('.deck-toolbar'))) {
                    // click inside menu or toolbar - do nothing
                    return;
                }
            } catch (err) {
                // ignore
            }
            setIsKebabMenuOpen({});
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [isKebabMenuOpen]);


    // Fetch deck previews for current page
    const fetchDeckPreviews = useCallback(() => {
        axios.get(`https://${host}:${port}/decks/previews?page=${currentPage}&limit=${decksPerPage}`)
            .then(resp => {
                const data = resp.data || {};
                setDecks(data.previews || []);
                setTotalPages(data.totalPages || 1);
                // Set deckPreviews state for rendering
                const previewsMap = {};
                (data.previews || []).forEach(deck => {
                    previewsMap[deck._id] = {
                        urls: (deck.previewCards || []).map(card => {
                            // Build image path from minimal card data
                            if (!card) return null;
                            const editionName = Array.isArray(card.edition) ? card.edition[0] : (card.edition || 'Unknown');
                            let img = card.imageUrl || null;
                            if (img) {
                                if (/^https?:\/\//i.test(img)) return img;
                                if (img.startsWith('/')) return img;
                                if (img.includes('assets/cards')) return `/${img.replace(/^\/+/,'')}`;
                                if (img.includes('/EN/') || img.includes('EN/')) return `/assets/cards/${img.replace(/^\/+/,'')}`;
                                return `/assets/cards/${editionName}/EN/${img}`;
                            }
                            const safeName = (card.name || '').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                            if (card.cardNumber) return `/assets/cards/${editionName}/EN/${card.cardNumber}_${safeName}_en.png`;
                            return null;
                        }),
                        total: deck.total
                    };
                });
                setDeckPreviews(previewsMap);
            })
            .catch(e => console.error(e));
    }, [host, port, currentPage, decksPerPage]);

    const buildImagePath = (cardObj) => {
        if (!cardObj) return null;
        const editionName = Array.isArray(cardObj?.edition) ? cardObj.edition[0] : (cardObj?.edition || (cardsData && cardsData.editions && cardsData.editions[0]) || 'Unknown');
        let img = cardObj.imageUrl || cardObj.imageURL || null;
        if (img) {
            if (/^https?:\/\//i.test(img)) return img;
            if (img.startsWith('/')) return img;
            if (img.includes('assets/cards')) return `/${img.replace(/^\/+/, '')}`;
            if (img.includes('/EN/') || img.includes('EN/')) return `/assets/cards/${img.replace(/^\/+/, '')}`;
            return `/assets/cards/${editionName}/EN/${img}`;
        }
        const safeName = (cardObj.name || '').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        if (cardObj.cardNumber) return `/assets/cards/${editionName}/EN/${cardObj.cardNumber}_${safeName}_en.png`;
        if (cardObj._id) return `/assets/cards/${editionName}/EN/${cardObj._id}.png`;
        return null;
    };

    const fetchPreviewForDeck = useCallback(async (deckId) => {
        if (!deckId) return;
        try {
            const resp = await axios.get(`https://${host}:${port}/deck/view/${deckId}`);
            const payload = resp.data?.deck || resp.data || {};
            const cards = payload.cards || [];
            const pickIndexes = [5, 8, 11];
            const urls = pickIndexes.map(i => {
                const entry = cards[i];
                if (!entry) return null;
                const cardObj = entry.card || entry;
                return buildImagePath(cardObj);
            });
            const total = cards.reduce((s, c) => s + (c.quantity || 1), 0);
            setDeckPreviews(prev => ({ ...prev, [deckId]: { urls, total } }));
        } catch (err) {
            setDeckPreviews(prev => ({ ...prev, [deckId]: { urls: [], total: 0 } }));
        } finally {
            activeCount.current = Math.max(0, activeCount.current - 1);
            if (pendingQueue.current.length > 0 && activeCount.current < CONCURRENCY) {
                const next = pendingQueue.current.shift();
                activeCount.current += 1;
                fetchPreviewForDeck(next);
            }
        }
    }, [host, port, cardsData]);

    const schedulePreviewFetch = useCallback((deckId) => {
        if (deckPreviews[deckId]) return;
        if (activeCount.current < CONCURRENCY) {
            activeCount.current += 1;
            fetchPreviewForDeck(deckId);
        } else {
            if (!pendingQueue.current.includes(deckId)) pendingQueue.current.push(deckId);
        }
    }, [deckPreviews, fetchPreviewForDeck]);

    useEffect(() => {
        if (typeof window === 'undefined' || observerRef.current) return;
        observerRef.current = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const id = entry.target.getAttribute('data-deck-id');
                if (entry.isIntersecting && id) {
                    schedulePreviewFetch(id);
                    try { observerRef.current.unobserve(entry.target); } catch (e) { }
                }
            });
        }, { root: null, rootMargin: '200px', threshold: 0.1 });
    }, [schedulePreviewFetch]);

    useEffect(() => {
        const refs = deckRefs.current || {};
        Object.keys(refs).forEach(id => { const el = refs[id]; if (el && observerRef.current) observerRef.current.observe(el); });
        return () => { if (observerRef.current) { Object.values(refs).forEach(el => { try { observerRef.current.unobserve(el); } catch (e) { } }); } };
    }, [decks]);

    useEffect(() => { fetchDeckPreviews(); }, [fetchDeckPreviews]);

    // Fetch and cache owner usernames for decks
    useEffect(() => {
        if (!decks || decks.length === 0) return;
        const ids = decks.map(d => {
            if (!d) return null;
            // possible places for owner id
            const creator = d.creator && (typeof d.creator === 'object' ? (d.creator._id || d.creator) : d.creator);
            const owner = d.owner && (typeof d.owner === 'object' ? (d.owner._id || d.owner) : d.owner);
            const userField = d.user && (typeof d.user === 'object' ? (d.user._id || d.user) : d.user);
            return creator || owner || userField || null;
        }).filter(Boolean).map(String);

        const uniqueIds = Array.from(new Set(ids));
        const missing = uniqueIds.filter(id => !ownerNames[id]);
        if (missing.length === 0) return;

        let cancelled = false;

        Promise.all(missing.map(id => axios.get(`https://${host}:${port}/user/view/${id}`).then(resp => ({ id, name: resp.data && (resp.data.username || resp.data.user?.username) || null })).catch(() => ({ id, name: null }))))
            .then(results => {
                if (cancelled) return;
                const map = {};
                results.forEach(r => { if (r && r.id && r.name) map[r.id] = r.name; });
                if (Object.keys(map).length > 0) setOwnerNames(prev => ({ ...prev, ...map }));
            });

        return () => { cancelled = true; };
    }, [decks, host, port, ownerNames]);

    const loadDeck = async (deckId) => {
        try {
            const response = await axios.get(`https://${host}:${port}/deck/view/${deckId}`);
            setSelectedDeckData(response.data);
            setIsModalDeckViewVisible(true);
        } catch (error) { console.error('Error fetching deck details:', error); }
    };

    const exportDeck = async (deckId, format) => {
        const url = `https://${host}:${port}/export/${format}/deck/${deckId}`;
        try {
            const res = await fetch(url, { method: 'GET' });
            if (!res.ok) throw new Error(`Error: ${res.status}`);
            const blob = await res.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl; link.download = `deck_${deckId}.${format}`; document.body.appendChild(link); link.click(); link.remove();
        } catch (error) { console.error('Failed to export deck:', error); }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).catch(err => console.error('Failed to copy: ', err));
    };

    const duplicateDeck = async (deckId) => {
        try {
            await axios.post(`https://${host}:${port}/deck/duplicate`, { userId: user._id, deckId });
            fetchDeckPreviews();
        } catch (error) { console.error('Error duplicating deck:', error); }
    };

    return (
        <div className="public-decklist-component">
            {isModalDeckViewVisible ? (
                <div className="deck-view d-flex flex-column align-items-center justify-content-center">
                    <div className="content">
                        <h1 className="text-white text-center fw-bold">{selectedDeckData?.deck?.name}</h1>
                        <hr className="w-50 mx-auto text-white" />
                        <DeckView cardsData={selectedDeckData?.deck} gameData={gameData} cAbilities={abilities} setisModalDeckViewVisible={true} setIsDeckBuilderVisible={false} />
                    </div>
                </div>
            ) : (
                <div className="public-decks-section mb-5">
                    <div>
                        <h1 className="text-white text-center fw-bold">Community Decks</h1>
                        <hr className="w-50 mx-auto text-white" />
                        <div className="container">
                            {/* Top pagination bar */}
                            {totalPages > 1 && (
                                <div className="pagination-public-decks d-flex justify-content-center align-items-center mt-3">
                                    <MobileView>
                                        <button className="btn btn-secondary mx-2" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Précédent</button>
                                        <span className="mx-2">Page {currentPage} / {totalPages}</span>
                                        <button className="btn btn-secondary mx-2" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Suivant</button>
                                    </MobileView>
                                    <BrowserView>
                                        <button className="btn btn-secondary mx-2" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Précédent</button>
                                        {[...Array(totalPages)].map((_, i) => (
                                            <button
                                                key={i}
                                                className={`btn mx-1 pagination-page-btn${currentPage === i + 1 ? ' active' : ''}`}
                                                onClick={() => setCurrentPage(i + 1)}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}
                                        <button className="btn btn-secondary mx-2" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Suivant</button>
                                    </BrowserView>
                                </div>
                            )}
                            <div className="row">
                                {paginatedDecks.map((deck, index) => {
                                    const preview = deckPreviews[deck._id];
                                    // If we've loaded a preview and it contains zero images, skip rendering this deck
                                    if (preview && (preview.total === 0 || (Array.isArray(preview.urls) && preview.urls.filter(Boolean).length === 0))) return null;

                                    const creator = deck.creator && (typeof deck.creator === 'object' ? (deck.creator._id || deck.creator) : deck.creator);
                                    const owner = deck.owner && (typeof deck.owner === 'object' ? (deck.owner._id || deck.owner) : deck.owner);
                                    const userField = deck.user && (typeof deck.user === 'object' ? (deck.user._id || deck.user) : deck.user);
                                    const ownerId = creator || owner || userField || null;
                                    const ownerDisplay = (ownerId && ownerNames[String(ownerId)]) || deck.ownerName || deck.owner?.username || deck.user?.username || deck.creator?.username || deck.creatorName || deck.createdBy?.username || deck.createdByName || 'Community';

                                    return (
                                        <div key={deck._id} className="col-12 col-md-4 mb-4" data-deck-id={deck._id} ref={el => { if (el) deckRefs.current[deck._id] = el; }}>
                                            <div className={`card deck-container text-center ${index % 2 === 0 ? 'me-lg-1' : ''}`}>
                                                <div className="card-header deck-title d-flex justify-content-between align-items-center">
                                                    <div className="deck-title-left">
                                                        <h3 className="card-title deck-name" title={deck.name || 'Untitled deck'}>{deck.name || 'Untitled deck'}</h3>
                                                        <div className="deck-owner small text-white-50">by {ownerDisplay}</div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                                                        <div className="deck-toolbar">
                                                            <FontAwesomeIcon icon={faEllipsisV} style={{ fontSize: '2rem', color: 'white', cursor: 'pointer' }} onClick={() => toggleKebabMenu(index)} />
                                                            {isKebabMenuOpen[index] && (
                                                                <div className="deck-kebab-menu">
                                                                    <div onClick={(e) => { e.stopPropagation(); exportDeck(deck._id, 'json'); }} title="Export as JSON"><FontAwesomeIcon icon={faFileExport} style={{ marginRight: '5px', fontSize: '2rem' }} /></div>
                                                                    <div onClick={(e) => { e.stopPropagation(); exportDeck(deck._id, 'txt'); }} title="Export as TXT"><FontAwesomeIcon icon={faFileAlt} style={{ marginRight: '5px', fontSize: '2rem' }} /></div>
                                                                    <div onClick={(e) => { e.stopPropagation(); copyToClipboard(deck._id); }} title="Copy TTS Export Number"><FontAwesomeIcon icon={faCopy} style={{ marginRight: '5px', fontSize: '2rem' }} /></div>
                                                                    {userIsLoggedIn && (<div onClick={(e) => { e.stopPropagation(); duplicateDeck(deck._id); }} title="Copy Deck"><FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px', fontSize: '2rem' }} /></div>)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="card-body deck-image" onClick={(e) => { e.preventDefault(); loadDeck(deck._id); }}>
                                                    <div className="error-deck">
                                                        {deck.haveError && errorDeckIndex === index && (
                                                            <div className="message alert alert-danger">{deck.listOfError.map((error, i) => (<span key={i}>| {error} |</span>))}</div>
                                                        )}
                                                    </div>

                                                    <div className="deck-preview">
                                                        {(deckPreviews[deck._id] && Array.isArray(deckPreviews[deck._id].urls) && deckPreviews[deck._id].urls.filter(Boolean).length > 0) ? (
                                                            deckPreviews[deck._id].urls.map((src, i) => (
                                                                <div key={i} className="deck-preview-slot">
                                                                    {src ? (
                                                                        <img src={src} alt={`card-${i}`} className={`deck-preview-img deck-preview-img-${i}`} />
                                                                    ) : (
                                                                        <img src={'/assets/images/back_cut.png'} alt={`card-back-${i}`} className={`deck-preview-img deck-preview-img-${i}`} />
                                                                    )}
                                                                    {i === 2 && typeof deckPreviews[deck._id]?.total === 'number' && (
                                                                        <div className="deck-preview-more">{deckPreviews[deck._id].total}</div>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            [0, 1, 2].map(i => (
                                                                <div key={i} className="deck-preview-slot"><img src={'/assets/images/back_cut.png'} alt={`card-back-${i}`} className={`deck-preview-img deck-preview-img-${i}`} /></div>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {totalPages > 1 && (
                                <div className="pagination-public-decks d-flex justify-content-center align-items-center mt-3">
                                    <MobileView>
                                        <button className="btn btn-secondary mx-2" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Précédent</button>
                                        <span className="mx-2">Page {currentPage} / {totalPages}</span>
                                        <button className="btn btn-secondary mx-2" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Suivant</button>
                                    </MobileView>
                                    <BrowserView>
                                        <button className="btn btn-secondary mx-2" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Précédent</button>
                                        {[...Array(totalPages)].map((_, i) => (
                                            <button
                                                key={i}
                                                className={`btn mx-1 pagination-page-btn${currentPage === i + 1 ? ' active' : ''}`}
                                                onClick={() => setCurrentPage(i + 1)}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}
                                        <button className="btn btn-secondary mx-2" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Suivant</button>
                                    </BrowserView>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default PublicDeckList;
